/**
 * EE 營運中心 — API service
 */

import axios from '../api/axios';
import {
  OpsCenter, OpsCenterCreate, OpsCenterUpdate,
  OpsMaterial, PresentationResponse,
  ImportResearchRequest, ImportResearchResult,
  OpsTag, OpsTagCreate, OpsTagUpdate,
  TagAggregateEntry, BulkAssignTagsResult,
} from '../types/opsCenter';

const BASE = '/api/ee/ops-center';

// ============================================================
// Centers
// ============================================================
export const listCenters = async (params?: {
  is_active?: boolean; search?: string;
}): Promise<OpsCenter[]> => {
  const r = await axios.get(`${BASE}/centers`, { params });
  return r.data;
};

export const getCenter = async (id: number): Promise<OpsCenter> => {
  const r = await axios.get(`${BASE}/centers/${id}`);
  return r.data;
};

export const getCenterByCode = async (code: string): Promise<OpsCenter> => {
  const r = await axios.get(`${BASE}/centers/by-code/${encodeURIComponent(code)}`);
  return r.data;
};

export const createCenter = async (data: OpsCenterCreate): Promise<OpsCenter> => {
  const r = await axios.post(`${BASE}/centers`, data);
  return r.data;
};

export const updateCenter = async (id: number, data: OpsCenterUpdate): Promise<OpsCenter> => {
  const r = await axios.put(`${BASE}/centers/${id}`, data);
  return r.data;
};

export const deleteCenter = async (id: number): Promise<void> => {
  await axios.delete(`${BASE}/centers/${id}`);
};

// ============================================================
// Materials
// ============================================================
export const listMaterials = async (
  centerId: number,
  params?: { is_active?: boolean; material_type?: string },
): Promise<OpsMaterial[]> => {
  const r = await axios.get(`${BASE}/centers/${centerId}/materials`, { params });
  return r.data;
};

/** 更新素材；category 為 source_metadata.category 的 shortcut（空字串清除分類） */
export interface MaterialUpdatePayload extends Partial<Pick<OpsMaterial,
  'title' | 'raw_text' | 'ai_summary' | 'order_index' | 'is_active'>> {
  category?: string;
}

export const updateMaterial = async (
  id: number,
  data: MaterialUpdatePayload,
): Promise<OpsMaterial> => {
  const r = await axios.put(`${BASE}/materials/${id}`, data);
  return r.data;
};

export const deleteMaterial = async (id: number): Promise<void> => {
  await axios.delete(`${BASE}/materials/${id}`);
};

/** 批次刪除素材（上限 100/次；超量由呼叫端分批）。 */
export const bulkDeleteMaterials = async (ids: number[]): Promise<void> => {
  await axios.post(`${BASE}/materials/bulk-delete`, { material_ids: ids });
};

export const importResearchItems = async (
  centerId: number,
  data: ImportResearchRequest,
): Promise<ImportResearchResult> => {
  const r = await axios.post(`${BASE}/centers/${centerId}/import-research`, data);
  return r.data;
};

// ============================================================
// Upload + AI 分析
// ============================================================
export interface UploadResult {
  ok: boolean;
  source_type?: string;
  filename?: string;
  created?: number;
  material_ids?: number[];
  ai_analyzed?: number;
  message: string;
  supported_types?: string[];
}

export const uploadFile = async (
  centerId: number,
  file: File,
  runAi = false,
  category?: string,
): Promise<UploadResult> => {
  const fd = new FormData();
  fd.append('file', file);
  const params = new URLSearchParams();
  params.set('run_ai', String(runAi));
  if (category && category.trim()) {
    params.set('category', category.trim());
  }
  const r = await axios.post(
    `${BASE}/centers/${centerId}/upload?${params.toString()}`,
    fd,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600_000,  // 10 min for big files
    },
  );
  return r.data;
};

export const analyzeMaterial = async (materialId: number): Promise<OpsMaterial> => {
  const r = await axios.post(`${BASE}/materials/${materialId}/analyze`, {}, {
    timeout: 120_000,
  });
  return r.data;
};

export const bulkAnalyze = async (
  centerId: number,
  limit = 50,
): Promise<{ ok: boolean; analyzed: number; message: string }> => {
  const r = await axios.post(
    `${BASE}/centers/${centerId}/bulk-analyze?limit=${limit}`,
    {},
    { timeout: 600_000 },
  );
  return r.data;
};

// ============================================================
// Presentation
// ============================================================
export const getPresentation = async (centerId: number): Promise<PresentationResponse> => {
  const r = await axios.get(`${BASE}/centers/${centerId}/present`);
  return r.data;
};

export const getMaterialImageUrl = (materialId: number): string => {
  return `${BASE}/materials/${materialId}/image`;
};

// ============================================================
// Tags（知識庫標籤字典）
// ============================================================
export const listTags = async (centerId: number,
                               params?: { is_active?: boolean }): Promise<OpsTag[]> => {
  const r = await axios.get(`${BASE}/centers/${centerId}/tags`, { params });
  return r.data;
};

export const createTag = async (centerId: number, data: OpsTagCreate): Promise<OpsTag> => {
  const r = await axios.post(`${BASE}/centers/${centerId}/tags`, data);
  return r.data;
};

export const updateTag = async (tagId: number, data: OpsTagUpdate): Promise<OpsTag> => {
  const r = await axios.put(`${BASE}/tags/${tagId}`, data);
  return r.data;
};

export const deleteTag = async (tagId: number): Promise<void> => {
  await axios.delete(`${BASE}/tags/${tagId}`);
};

/** 設定單筆 material 的 tags（覆蓋既有 tag 集合） */
export const setMaterialTags = async (materialId: number, tagIds: number[]): Promise<number[]> => {
  const r = await axios.put(`${BASE}/materials/${materialId}/tags`, { tag_ids: tagIds });
  return r.data;
};

/** 批次掛 tags（追加，不移除既有 tags） */
export const bulkAssignTags = async (
  centerId: number,
  materialIds: number[],
  tagIds: number[],
): Promise<BulkAssignTagsResult> => {
  const r = await axios.post(
    `${BASE}/centers/${centerId}/tags-bulk-assign`,
    { material_ids: materialIds, tag_ids: tagIds },
  );
  return r.data;
};

/** Tag 彙整報告 */
export const aggregateTags = async (centerId: number): Promise<TagAggregateEntry[]> => {
  const r = await axios.get(`${BASE}/centers/${centerId}/tags/aggregate`);
  return r.data;
};

/** 取得指定 tag 的所有素材 */
export const listMaterialsByTag = async (centerId: number, tagId: number): Promise<OpsMaterial[]> => {
  const r = await axios.get(`${BASE}/centers/${centerId}/tags/${tagId}/materials`);
  return r.data;
};
